import dcmjs from 'dcmjs';
import {
    formatTag,
    isPrivateTag,
    parseTag,
    punctuateTag,
    Tag,
    tagElement,
} from './tag';
import { resolveVr, Vr } from './vr';

/**
 * Standard data dictionary lookups: a tag's Value Representation and its
 * keyword.
 *
 * An implicit-VR stream carries no VR on the wire, so the dictionary is what
 * makes such a dataset interpretable at all — including deciding which
 * attributes are sequences and must be descended into.
 *
 * The dictionary is reference data, not behaviour, and dcmjs already ships a
 * complete and maintained copy of it. This module is the single point at
 * which the engine consumes it, so replacing it with a generated table is a
 * change to this file alone.
 */

interface DictionaryEntry {
    readonly vr: Vr;
    /**
     * The dictionary's own VR string, before ambiguous codes are resolved.
     *
     * Kept because 'ox' and 'xs' are not merely unresolved VRs — they are the
     * marker that an attribute's representation is data-dependent, and a
     * consumer that has to reproduce dcmjs's `_vrMap` needs to see the marker
     * rather than the resolution.
     */
    readonly rawVr: string;
    readonly keyword: string;
}

const entryCache = new Map<Tag, DictionaryEntry | undefined>();

function lookup(tag: Tag): DictionaryEntry | undefined {
    const cached = entryCache.get(tag);
    if (cached !== undefined || entryCache.has(tag)) {
        return cached;
    }
    const raw = dcmjs.data.DicomMetaDictionary.dictionary[punctuateTag(tag)];
    const entry: DictionaryEntry | undefined = raw
        ? { vr: resolveVr(raw.vr), rawVr: raw.vr, keyword: raw.name }
        : undefined;
    entryCache.set(tag, entry);
    return entry;
}

/**
 * The Value Representation an implicit-VR stream implies for this tag.
 *
 * Unknown and private attributes resolve to UN, whose value is an opaque byte
 * stream: the bytes survive a read/write cycle intact, and nothing pretends
 * to interpret data whose meaning is not known.
 *
 * Item and delimiter tags in group FFFE are structural rather than
 * attributes; the parser handles them before consulting the dictionary and
 * they are not expected here.
 */
export function vrForTag(tag: Tag): Vr {
    const entry = lookup(tag);
    if (entry) {
        return entry.vr;
    }
    // Group lengths are always UL even where the dictionary has retired the
    // individual entry.
    if (tagElement(tag) === 0x0000) {
        return 'UL';
    }
    // A private creator — odd group, element 0x0010 to 0x00FF — names the
    // organisation that owns a block of private tags, and PS3.5 section 7.8.1
    // fixes it as LO. Without this it reads as UN, and the owner's name comes
    // back as opaque bytes that no caller can match a private block against.
    if (isPrivateTag(tag)) {
        const element = tagElement(tag);
        if (element >= 0x0010 && element <= 0x00ff) {
            return 'LO';
        }
    }
    return 'UN';
}

/**
 * The dictionary's unresolved VR string for a tag, or undefined when the tag
 * is not in the dictionary. 'ox' and 'xs' mean the representation depends on
 * another attribute's value.
 */
export function rawDictionaryVrForTag(tag: Tag): string | undefined {
    return lookup(tag)?.rawVr;
}

/**
 * The dictionary keyword for a tag ("PatientName"), or undefined for private
 * and unrecognized attributes.
 *
 * Keywords are for display and for the compatibility projection only. The
 * engine's own model is keyed by tag, so an attribute missing from the
 * dictionary is still fully addressable.
 */
export function keywordForTag(tag: Tag): string | undefined {
    return lookup(tag)?.keyword;
}

/**
 * Resolves a dictionary keyword back to its tag.
 *
 * Only a keyword resolves. A naturalized dataset keys an attribute the
 * dictionary does not know — every private attribute among them — by its raw
 * tag instead, and that key deliberately does not resolve here: dcmjs's
 * denaturalization drops such an attribute, and a writer standing in for it
 * has to make the same choice or it would emit files with attributes the
 * application has never written. See `denaturalizeDataset` for what that
 * costs.
 */
export function tagForKeyword(keyword: string): Tag | undefined {
    const entry = dcmjs.data.DicomMetaDictionary.nameMap[keyword];
    return entry === undefined ? undefined : parseTag(entry.tag);
}

/**
 * A stable display name for a tag: its keyword where the dictionary has one,
 * otherwise the unpunctuated tag marked as private or unknown.
 */
export function displayNameForTag(tag: Tag): string {
    const keyword = keywordForTag(tag);
    if (keyword !== undefined) {
        return keyword;
    }
    return isPrivateTag(tag)
        ? `PrivateTag${formatTag(tag)}`
        : `UnknownTag${formatTag(tag)}`;
}
